import { ExitToApp, Launch } from '@mui/icons-material';
import {
  Avatar,
  Box,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppSetSelector } from '../../../../../components/AppSetSelector';
import {
  AnalyticsIcon,
  APIIcon,
  CatalogIcon,
  CollectionsIcon,
  DocsIcon,
  HomepageIcon,
  SearchIcon,
  SetupGuideIcon,
  TeamIcon,
} from '../../../../../components/icons';
import { useStudioAuthenticatedContext } from '../../../../../context/StudioContext';
import { UI_CONSTANTS } from '../../../../../theme/constants';
import { useCatalogEnabled } from '../../../../../util/useCatalogEnabled';
import { useFeatureFlags } from '../../../../../util/useFeatureFlags';
import styles from './NavigationSection.module.css';

export const NavigationSection = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, logout } = useStudioAuthenticatedContext();
  const { isEnabled } = useFeatureFlags();
  const catalogEnabled = useCatalogEnabled();

  const menuItems = [
    {
      icon: <HomepageIcon />,
      label: 'Dashboard',
      path: '/dashboard',
      active: location.pathname === '/',
    },
    {
      icon: <CollectionsIcon />,
      label: 'Collections',
      path: '/collections',
      active: location.pathname === '/collections',
    },
    ...(isEnabled('searchPage')
      ? [
          {
            icon: <SearchIcon />,
            label: 'Similarity Search',
            path: '/search',
            active: location.pathname === '/search',
          },
        ]
      : []),
    ...(catalogEnabled
      ? [
          {
            icon: <CatalogIcon />,
            label: 'Catalog',
            path: '/catalog',
            active: location.pathname === '/catalog',
          },
        ]
      : []),
    {
      icon: <AnalyticsIcon />,
      label: 'Analytics',
      path: '/analytics',
      active: location.pathname === '/analytics',
    },
    {
      icon: <APIIcon />,
      label: 'API',
      path: '/api',
      active: location.pathname === '/api',
    },
    {
      icon: <TeamIcon />,
      label: 'Team',
      path: '/team',
      active: false,
      hidden: true,
    },
    {
      icon: <DocsIcon />,
      label: 'Docs',
      path: '/docs',
      active: false,
      external: true,
    },
    {
      icon: <SetupGuideIcon />,
      label: 'Setup Guide',
      path: 'https://www.feed.fm/feed-clips-implementation-guide',
      active: false,
      external: true,
    },
  ];

  const userProfile = {
    name: `${currentUser!.firstName} ${currentUser!.lastName}`,
    role: 'Account Owner', // This could be dynamic based on user role if available
    initials: `${currentUser!.firstName.charAt(0)}${currentUser!.lastName.charAt(0)}`,
  };

  const handleNavigation = (path: string, external?: boolean) => {
    if (external) {
      window.open(path, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate(path);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <Drawer
      variant="permanent"
      className={styles.navigationDrawer}
      sx={{
        width: UI_CONSTANTS.NAVIGATION_DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: UI_CONSTANTS.NAVIGATION_DRAWER_WIDTH,
          boxSizing: 'border-box',
          backgroundColor: theme.palette.background.paper,
        },
      }}
    >
      {/* Logo section */}
      <Box className={styles.logoSection}>
        <Box
          component="img"
          src={
            theme.palette.mode === 'dark'
              ? '/fmg-clips-logo-dark.svg'
              : '/fmg-clips-logo-light.svg'
          }
          alt="FeedClips logo"
          className={styles.logoImage}
        />
      </Box>

      {/* App Set Selector */}
      <AppSetSelector />

      {/* Navigation menu */}
      <Box className={styles.navigationMenu}>
        <List>
          {menuItems
            .filter((item) => !item.hidden)
            .map((item, index) => (
              <ListItem key={index} disablePadding>
                <ListItemButton
                  selected={item.active}
                  onClick={() => handleNavigation(item.path, item.external)}
                  className={styles.navigationItem}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      className: styles.navigationItemText,
                    }}
                  />
                  {item.external && (
                    <ListItemIcon>
                      <Launch className={styles.externalIcon} />
                    </ListItemIcon>
                  )}
                </ListItemButton>
              </ListItem>
            ))}
        </List>
      </Box>

      {/* User profile section */}
      <Box className={styles.userProfileSection}>
        <Box className={styles.userProfileContainer}>
          <Avatar className={styles.userAvatar}>{userProfile.initials}</Avatar>
          <Box className={styles.userInfo}>
            <Typography variant="body2" className={styles.userName}>
              {userProfile.name}
            </Typography>
            <Typography variant="caption" className={styles.userRole}>
              {userProfile.role}
            </Typography>
          </Box>
        </Box>
        <Button
          variant="outlined"
          fullWidth
          startIcon={<ExitToApp />}
          onClick={handleLogout}
          className={styles.logoutButton}
        >
          Sign Out
        </Button>
      </Box>
    </Drawer>
  );
};
